import React from "react";
import { ModeRendererProps } from "./types";

// VASARELY × VAN GOGH: Emotion through movement
// Vortex strokes, vivid texture, turbulent flows
export function VasarelyVanGoghMode({ brand }: ModeRendererProps) {
  return (
    <g>
      {/* Turbulent background */}
      <rect x="0" y="0" width="794" height="1123" fill={brand.deep} />

      <g transform="translate(397 540)">
        {/* Vortex stroke pattern (starry night inspired) */}
        {Array.from({ length: 50 }).map((_, i) => {
          const angle = i * 0.7;
          const r = 30 + i * 6;
          const x = Math.cos(angle) * 20;
          const y = Math.sin(angle) * 20;
          
          return (
            <ellipse
              key={i}
              cx={x}
              cy={y}
              rx={r}
              ry={r * 0.6}
              fill="none"
              stroke={i % 3 === 0 ? brand.amber : i % 3 === 1 ? brand.aqua : brand.violet}
              strokeWidth="3"
              opacity={0.5 - i * 0.008}
              transform={`rotate(${angle * 15} ${x} ${y})`}
            />
          );
        })}

        {/* Expressive brush stroke grid */}
        {Array.from({ length: 14 }).map((_, i) =>
          Array.from({ length: 18 }).map((_, j) => {
            const x = (i - 6.5) * 42;
            const y = (j - 8.5) * 40;
            const motion = Math.sin(i * 0.6) * 15 + Math.cos(j * 0.5) * 12;
            const angle = Math.atan2(y, x) * 20;
            const colors = [brand.amber, brand.aqua, brand.violet];
            const colorIndex = Math.floor((i * j) % 3);
            
            return (
              <rect
                key={`${i}-${j}`}
                x={x - 14 + motion}
                y={y - 14}
                width="28"
                height="28"
                fill={colors[colorIndex]}
                opacity={0.3}
                transform={`rotate(${angle} ${x} ${y})`}
              />
            );
          })
        )}

        {/* Kinetic circular flows */}
        {Array.from({ length: 8 }).map((_, i) => {
          const angle = (i * Math.PI) / 4;
          const centerX = Math.cos(angle) * 160;
          const centerY = Math.sin(angle) * 160;
          
          return (
            <g key={i}>
              {Array.from({ length: 10 }).map((_, j) => {
                const spiral = j * 0.8;
                const r = 15 + j * 8;
                const sx = centerX + Math.cos(spiral) * 5;
                const sy = centerY + Math.sin(spiral) * 5;
                
                return (
                  <circle
                    key={j}
                    cx={sx}
                    cy={sy}
                    r={r}
                    fill="none"
                    stroke={j % 2 === 0 ? brand.amber : brand.aqua}
                    strokeWidth="2"
                    opacity={0.4 - j * 0.03}
                  />
                );
              })}
            </g>
          );
        })}

        {/* Textured stroke lines */}
        {Array.from({ length: 20 }).map((_, i) => {
          const angle = (i * Math.PI) / 10;
          const length = 180 + Math.sin(i) * 40;
          const curve = Math.sin(i * 0.5) * 60;
          
          return (
            <path
              key={i}
              d={`M 0 0 Q ${Math.cos(angle) * (length / 2) + curve} ${Math.sin(angle) * (length / 2)} ${Math.cos(angle) * length} ${Math.sin(angle) * length}`}
              fill="none"
              stroke={i % 3 === 0 ? brand.amber : i % 3 === 1 ? brand.violet : brand.aqua}
              strokeWidth="4"
              opacity="0.3"
            />
          );
        })}

        {/* Emotional energy nodes (cypress tree metaphor) */}
        {[
          { x: 0, y: -180, h: 120 },
          { x: -140, y: 60, h: 100 },
          { x: 140, y: 80, h: 110 },
        ].map((tree, i) => (
          <g key={i}>
            <ellipse
              cx={tree.x}
              cy={tree.y}
              rx="35"
              ry={tree.h}
              fill={brand.violet}
              opacity="0.3"
            />
            {Array.from({ length: 8 }).map((_, j) => {
              const y = tree.y - tree.h / 2 + (j * tree.h) / 8;
              const wave = Math.sin(j * 0.5) * 15;
              return (
                <line
                  key={j}
                  x1={tree.x - 30 + wave}
                  y1={y}
                  x2={tree.x + 30 + wave}
                  y2={y}
                  stroke={brand.aqua}
                  strokeWidth="2"
                  opacity="0.4"
                />
              );
            })}
          </g>
        ))}

        {/* Swirling sky pattern */}
        {Array.from({ length: 6 }).map((_, i) => {
          const y = -200 + i * 70;
          const points = Array.from({ length: 40 }).map((_, j) => {
            const x = -280 + j * 14;
            const wave = Math.sin(j * 0.4 + i * 0.6) * 40;
            return `${x},${y + wave}`;
          }).join(" ");
          
          return (
            <polyline
              key={i}
              points={points}
              fill="none"
              stroke={i % 2 === 0 ? brand.amber : brand.violet}
              strokeWidth="4"
              opacity="0.3"
            />
          );
        })}
      </g>

      {/* Emotional inscription */}
      <text x="397" y="1040" textAnchor="middle" fontFamily="serif" fontSize="11" fill={brand.amber} opacity="0.6" fontStyle="italic">
        beweging en emotie
      </text>
    </g>
  );
}
